import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Controller, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { useCaseUserClientDetails } from 'hooks/useCaseUserClientDetails';
import { listLawftIds } from 'services/Settings';
import { totalCountEvent } from 'services/generalServices';
import { generateFullName } from 'utils/generateFullNameUtils';
import { PI_PROVIDERS_TYPE } from 'constants/index';

const AddInsurerDialog = ({
  visible,
  onHide,
  onSubmit,
  requiredFields = [],
  showInsurerDropdown = false,
  caseId,
  caseDetails,
  insuranceProviders = [],
  onInsuranceAdded, // Callback to refresh insurance list after adding
  availableLawftIds = [], // Pre-filtered Lawft IDs from parent component
}) => {
  const [lawftIdList, setLawftIdList] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      insured_person: '',
      insurance_company: '',
      contact_name: '',
      email: '',
      phone: '',
      additional_phone: '',
      fax: '',
      policy_number: '',
      claim_number: '',
      insurance_limit: '',
      lawft_id: [],
    },
  });

  // Fetch insurer options from API
  const { caseUserClientList, loadCaseUserClientDetails } = useCaseUserClientDetails();

  useEffect(() => {
    if (visible && showInsurerDropdown && caseId) {
      loadCaseUserClientDetails(
        caseId,
        true, // is_contact_list
        false, // is_team_member_list
        true, // all_contacts - fetch all contact types (person, business, location)
        false, // contact_id
        false, // is_lead
        false, // is_mandatory_lawft_access
        false, // is_active
        false, // is_include_groups
        false // active_groups
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, showInsurerDropdown, caseId]);

  // Load Lawft IDs
  useEffect(() => {
    if (visible && caseDetails) {
      const practice_area = {
        practice_area: [`${caseDetails?.case_description?.case_practice_area}`],
        show_unassigned_pa_column: true,
      };

      totalCountEvent('lawft-id')
        .then((response) => {
          listLawftIds(response.data, '', 'forward', 'ACTIVE', practice_area)
            .then((response) => {
              const lawftIds =
                response?.data?.lawft_id.map((lawftId) => ({
                  label: lawftId.lawft_id_name,
                  value: lawftId.lawft_id || '',
                })) || [];
              setLawftIdList(lawftIds);
            })
            .catch((err) => {
              console.error('Error loading lawft IDs:', err);
              setLawftIdList([]);
            });
        })
        .catch((err) => {
          console.error('Error getting lawft-id count:', err);
        });
    }
  }, [visible, caseDetails]);

  // Format insurer options for dropdown using useMemo to prevent unnecessary recalculations
  const insurerOptions = useMemo(() => {
    if (!caseDetails) return [];

    // Build list from both client_details and relations, similar to RelatedContacts
    const allContacts = [];

    // Add client details
    if (caseDetails?.client_details) {
      caseDetails.client_details.forEach((client) => {
        // Only include person and business, exclude location
        if (client.contact_type === 'person' || client.contact_type === 'business') {
          allContacts.push(client);
        }
      });
    }

    // Add relations (excluding PI providers and locations)
    if (caseDetails?.relations) {
      caseDetails.relations.forEach((relation) => {
        // Only include person and business contacts, exclude location and PI providers
        if (
          (relation.contact_type === 'person' || relation.contact_type === 'business') &&
          !PI_PROVIDERS_TYPE.includes(relation.contact_type)
        ) {
          allContacts.push(relation);
        }
      });
    }

    // Remove duplicates based on contact_id
    const uniqueContacts = allContacts.filter(
      (contact, index, self) => index === self.findIndex((c) => c.contact_id === contact.contact_id)
    );

    // Map to dropdown options
    const options = uniqueContacts.map((contact) => {
      // Use generateFullName which properly handles title + first_name + middle_name + last_name
      const displayName = generateFullName(contact);

      return {
        label: displayName || contact.email || 'Unknown Contact',
        value: contact.contact_id || contact.user_id,
        contactData: contact, // Store full contact data for later use
      };
    });

    return options;
  }, [caseDetails]);

  // Get all insurer IDs currently used by insurance providers
  const getUsedInsurerIds = useCallback(() => {
    const usedIds = [];
    insuranceProviders.forEach((provider) => {
      // insured_person can be either an object with contact_id property or a string contact_id
      if (provider?.insured_person) {
        if (typeof provider.insured_person === 'string') {
          usedIds.push(provider.insured_person);
        } else if (provider.insured_person?.contact_id) {
          usedIds.push(provider.insured_person.contact_id);
        }
      }
      // Also check insured_contact_id field as fallback
      if (provider?.insured_contact_id) {
        usedIds.push(provider.insured_contact_id);
      }
    });
    return usedIds;
  }, [insuranceProviders]);

  // Filter out insurers that are already used by other insurance providers
  const filteredInsurerOptions = useMemo(() => {
    const usedInsurerIds = getUsedInsurerIds();
    return insurerOptions.filter((insurer) => !usedInsurerIds.includes(insurer?.value));
  }, [insurerOptions, getUsedInsurerIds]);

  // Get all lawft IDs currently used by other insurance providers
  const getUsedLawftIds = useCallback(() => {
    const usedIds = [];
    insuranceProviders.forEach((provider) => {
      if (provider?.lawft_id && Array.isArray(provider.lawft_id)) {
        usedIds.push(...provider.lawft_id);
      }
    });
    return usedIds;
  }, [insuranceProviders]);

  // Filter out lawft IDs that are already used
  // If availableLawftIds is provided from parent, use it (it already filters Person, Business, and Insurance Providers)
  // Otherwise, fall back to the local filtering (only filters other insurance providers)
  const filteredLawftIdList = useMemo(() => {
    if (availableLawftIds && availableLawftIds.length > 0) {
      return availableLawftIds;
    }
    // Fallback to local filtering if availableLawftIds is not provided or empty
    const usedIds = getUsedLawftIds();
    return lawftIdList.filter((lawftId) => !usedIds.includes(lawftId?.value));
  }, [availableLawftIds, lawftIdList, getUsedLawftIds]);

  // Helper function to check if a field is required
  const isRequired = (fieldName) => requiredFields.includes(fieldName);

  const handleFormSubmit = async (data) => {
    await onSubmit(data);
    reset();
    // Call the callback to refresh insurance providers list
    if (onInsuranceAdded) {
      await onInsuranceAdded();
    }
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: '650px' }}
      onHide={handleClose}
      draggable={false}
      header={
        <div className="d-flex align-items-center">
          <span
            style={{
              backgroundColor: '#ef4444',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              marginRight: '12px',
              cursor: 'pointer',
            }}
            onClick={handleClose}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444';
            }}
          ></span>
        </div>
      }
      closable={false}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="modal-heading F-size16 text-bold">Add Insurance</div>
        </div>
        <div className="row">
          {/* Insured Person Dropdown - Only show in Contacts section */}
          {showInsurerDropdown && (
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <label className="p-medium mb-0">Insurer{isRequired('insured_person') && <span className="text-danger">*</span>}</label>
              </div>
              <div className="col-8">
                <Controller
                  name="insured_person"
                  control={control}
                  rules={isRequired('insured_person') ? { required: 'Insured person is required' } : {}}
                  render={({ field }) => (
                    <>
                      <Dropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.value)}
                        options={filteredInsurerOptions}
                        placeholder={filteredInsurerOptions.length > 0 ? 'Select Insurer' : 'No insurers available'}
                        className={`w-100 ${errors.insured_person ? 'p-invalid' : ''}`}
                        disabled={filteredInsurerOptions.length === 0}
                        emptyMessage="All available insurers have been assigned"
                      />
                      {filteredInsurerOptions.length === 0 && (
                        <small className="text-warning d-block mt-1">
                          All available insurers have already been assigned to insurance providers. Please add a new contact first.
                        </small>
                      )}
                      {errors.insured_person && <small className="p-error">{errors.insured_person.message}</small>}
                    </>
                  )}
                />
              </div>
            </div>
          )}

          {/* Insurance Company */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">
                Insurance Company{isRequired('insurance_company') && <span className="text-danger">*</span>}
              </label>
            </div>
            <div className="col-8">
              <Controller
                name="insurance_company"
                control={control}
                rules={isRequired('insurance_company') ? { required: 'Insurance company is required' } : {}}
                render={({ field }) => (
                  <>
                    <InputText
                      {...field}
                      className={`w-100 ${errors.insurance_company ? 'p-invalid' : ''}`}
                      placeholder="Enter insurance company name"
                    />
                    {errors.insurance_company && <small className="p-error">{errors.insurance_company.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Contact Name */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Contact{isRequired('contact_name') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="contact_name"
                control={control}
                rules={isRequired('contact_name') ? { required: 'Contact name is required' } : {}}
                render={({ field }) => (
                  <>
                    <InputText {...field} className={`w-100 ${errors.contact_name ? 'p-invalid' : ''}`} placeholder="Enter contact name" />
                    {errors.contact_name && <small className="p-error">{errors.contact_name.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Email */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Email{isRequired('email') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="email"
                control={control}
                rules={{
                  ...(isRequired('email') && { required: 'Email is required' }),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field }) => (
                  <>
                    <InputText {...field} className={`w-100 ${errors.email ? 'p-invalid' : ''}`} placeholder="Enter email" />
                    {errors.email && <small className="p-error">{errors.email.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Phone */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Phone{isRequired('phone') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="phone"
                control={control}
                rules={{
                  ...(isRequired('phone') && { required: 'Phone is required' }),
                  minLength: { value: 10, message: 'Phone number must be 10 digits' },
                }}
                render={({ field }) => (
                  <>
                    <NumberFormat
                      className={`input-shadow w-100 p-2 border-0 number-format p-inputtext ${errors.phone ? 'p-invalid' : ''}`}
                      placeholder="(###) ###-####"
                      format="(###) ###-####"
                      mask="_"
                      name="phone"
                      keyfilter={'pnum'}
                      onValueChange={(e) => field.onChange(e.value)}
                      value={field.value}
                    />
                    {errors.phone && <small className="p-error">{errors.phone.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Additional Phone */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Additional Phone</label>
            </div>
            <div className="col-8">
              <Controller
                name="additional_phone"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value && value.length > 0 && value.length < 10) {
                      return 'Phone number must be 10 digits';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <>
                    <NumberFormat
                      className={`input-shadow w-100 p-2 border-0 number-format p-inputtext ${errors.additional_phone ? 'p-invalid' : ''}`}
                      placeholder="(###) ###-####"
                      format="(###) ###-####"
                      mask="_"
                      name="additional_phone"
                      keyfilter={'pnum'}
                      onValueChange={(e) => field.onChange(e.value)}
                      value={field.value}
                    />
                    {errors.additional_phone && <small className="p-error">{errors.additional_phone.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Fax */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Fax{isRequired('fax') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="fax"
                control={control}
                rules={isRequired('fax') ? { required: 'Fax is required' } : {}}
                render={({ field }) => (
                  <>
                    <InputText {...field} className={`w-100 ${errors.fax ? 'p-invalid' : ''}`} placeholder="Enter fax number" />
                    {errors.fax && <small className="p-error">{errors.fax.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Policy Number */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Policy Number{isRequired('policy_number') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="policy_number"
                control={control}
                rules={isRequired('policy_number') ? { required: 'Policy number is required' } : {}}
                render={({ field }) => (
                  <>
                    <InputText
                      {...field}
                      className={`w-100 ${errors.policy_number ? 'p-invalid' : ''}`}
                      placeholder="Enter policy number"
                    />
                    {errors.policy_number && <small className="p-error">{errors.policy_number.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Claim Number */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Claim Number{isRequired('claim_number') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="claim_number"
                control={control}
                rules={isRequired('claim_number') ? { required: 'Claim number is required' } : {}}
                render={({ field }) => (
                  <>
                    <InputText {...field} className={`w-100 ${errors.claim_number ? 'p-invalid' : ''}`} placeholder="Enter claim number" />
                    {errors.claim_number && <small className="p-error">{errors.claim_number.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Insurance Limit */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">
                Insurance Limit{isRequired('insurance_limit') && <span className="text-danger">*</span>}
              </label>
            </div>
            <div className="col-8">
              <Controller
                name="insurance_limit"
                control={control}
                rules={isRequired('insurance_limit') ? { required: 'Insurance limit is required' } : {}}
                render={({ field }) => (
                  <>
                    <InputText
                      {...field}
                      keyfilter="pint"
                      className={`w-100 ${errors.insurance_limit ? 'p-invalid' : ''}`}
                      placeholder="Enter insurance limit"
                      onKeyDown={(e) => {
                        // Prevent 'e', 'E', '+', '-' which are allowed in type="number"
                        if (['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {errors.insurance_limit && <small className="p-error">{errors.insurance_limit.message}</small>}
                  </>
                )}
              />
            </div>
          </div>

          {/* Lawft ID */}
          <div className="row align-items-center mb-3">
            <div className="col-4">
              <label className="p-medium mb-0">Lawft ID{isRequired('lawft_id') && <span className="text-danger">*</span>}</label>
            </div>
            <div className="col-8">
              <Controller
                name="lawft_id"
                control={control}
                rules={isRequired('lawft_id') ? { required: 'Lawft ID is required' } : {}}
                render={({ field }) => (
                  <>
                    <MultiSelect
                      {...field}
                      options={filteredLawftIdList}
                      placeholder={filteredLawftIdList.length > 0 ? 'Select Lawft ID' : 'No Lawft IDs available'}
                      className={`w-100 ${errors.lawft_id ? 'p-invalid' : ''}`}
                      filter
                      display="chip"
                      disabled={filteredLawftIdList.length === 0}
                      emptyFilterMessage="No Lawft IDs available"
                    />
                    {filteredLawftIdList.length === 0 && (
                      <small className="text-warning d-block mt-1">
                        All available Lawft IDs have already been assigned to insurance providers.
                      </small>
                    )}
                    {errors.lawft_id && <small className="p-error">{errors.lawft_id.message}</small>}
                  </>
                )}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button label="Cancel" className="p-button-secondary outline" onClick={handleClose} type="button" style={{ width: '100px' }} />
          <Button
            label="Save"
            className="p-button-secondary"
            style={{ width: '100px' }}
            disabled={showInsurerDropdown && filteredInsurerOptions.length === 0}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default AddInsurerDialog;
